// Dev-only route for iterating on WaveformPad + TrimControls without
// the full forge shell. Hit /?forge=1 to open.
//
// Synthesizes a predictable stereo buffer (sine + click) so we can see
// markers land on meaningful features. Non-destructive: the trim state
// is applied in a preview transform chain and the resulting buffer is
// shown in a second pad below the source.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fade,
  normalize,
  reverse,
  trim,
  toPcm,
  fromPcm,
  type PcmBuffer,
} from '../pipeline';
import { TrimControls, DEFAULT_TRIM_STATE, type TrimState } from './TrimControls';
import { WaveformPad, type MarkerKind } from './WaveformPad';

const SAMPLE_RATE = 48000;
const DURATION_SECONDS = 2;

function synthesizeDemoBuffer(ctx: AudioContext): AudioBuffer {
  const frames = DURATION_SECONDS * SAMPLE_RATE;
  const buf = ctx.createBuffer(2, frames, SAMPLE_RATE);
  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);
  for (let i = 0; i < frames; i++) {
    const t = i / SAMPLE_RATE;
    // Fundamental + slight detune for a visibly fatter waveform.
    const s =
      Math.sin(2 * Math.PI * 440 * t) * 0.5 +
      Math.sin(2 * Math.PI * 442 * t) * 0.2;
    // Exponential amplitude envelope so the clip visibly decays.
    const env = Math.exp(-t * 1.2);
    L[i] = s * env;
    R[i] = s * env * 0.8;
  }
  // Insert a brief click at 1s so the waveform has an obvious feature
  // the user can aim markers at.
  const clickFrame = SAMPLE_RATE;
  for (let i = 0; i < 400; i++) {
    const v = Math.sin((i / 8) * Math.PI) * 0.9;
    L[clickFrame + i] = v;
    R[clickFrame + i] = v;
  }
  return buf;
}

export function ForgeDemo() {
  const [ctx, setCtx] = useState<AudioContext | null>(null);
  const [srcBuffer, setSrcBuffer] = useState<AudioBuffer | null>(null);
  const [inFrame, setInFrame] = useState(0);
  const [outFrame, setOutFrame] = useState(0);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [trimState, setTrimState] = useState<TrimState>(DEFAULT_TRIM_STATE);

  // StrictMode replays effects synchronously in dev, and state updates
  // from the first run aren't visible inside the captured closure during
  // the second run. A ref is the reliable "we've already started init"
  // signal — without it, we'd create two AudioContexts and leak one.
  const initStartedRef = useRef(false);

  const init = useCallback(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;
    const AudioCtor: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const newCtx = new AudioCtor();
    const buf = synthesizeDemoBuffer(newCtx);
    setCtx(newCtx);
    setSrcBuffer(buf);
    setOutFrame(buf.length);
  }, []);

  useEffect(() => {
    // Try to init immediately; if the browser refuses (some engines do
    // before a gesture), we fall through and wait for the button click.
    init();
  }, [init]);

  // Apply the trim chain to produce the preview buffer.
  const previewBuffer = useMemo(() => {
    if (!srcBuffer || !ctx) return null;
    let pcm: PcmBuffer = toPcm(srcBuffer);
    pcm = trim(pcm, {
      startSeconds: inFrame / srcBuffer.sampleRate,
      endSeconds: outFrame / srcBuffer.sampleRate,
    });
    pcm = fade(pcm, {
      inSeconds: trimState.fadeInMs / 1000,
      outSeconds: trimState.fadeOutMs / 1000,
    });
    if (trimState.normalize) pcm = normalize(pcm);
    if (trimState.reverse) pcm = reverse(pcm);
    return fromPcm(ctx, pcm);
  }, [srcBuffer, ctx, inFrame, outFrame, trimState]);

  const handleMarker = useCallback(
    (kind: MarkerKind, frame: number) => {
      if (kind === 'in') setInFrame(frame);
      else setOutFrame(frame);
    },
    [],
  );

  const ready = srcBuffer !== null && ctx !== null;

  return (
    <div className="min-h-dvh bg-zinc-950 text-zinc-200 p-6 space-y-6">
      <header className="space-y-1">
        <h1 className="text-lg uppercase tracking-[0.3em] text-yellow-300">
          Forge Demo
        </h1>
        <p className="text-xs text-zinc-500">
          Waveform pad + trim controls on a synthesized 2-second buffer. Dev-only; not linked from nav. Hit <kbd className="font-mono">Tab</kbd> then use <kbd className="font-mono">space</kbd> / <kbd className="font-mono">[</kbd> / <kbd className="font-mono">]</kbd>.
        </p>
      </header>

      {!ready && (
        <button
          onClick={init}
          className="px-3 py-2 bg-yellow-300 text-zinc-950 text-xs uppercase tracking-[0.2em]"
        >
          Start (initialize AudioContext)
        </button>
      )}

      <section className="space-y-2" data-testid="source-section">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Source
        </div>
        <WaveformPad
          buffer={srcBuffer}
          inMarkerFrame={inFrame}
          outMarkerFrame={outFrame}
          loopEnabled={loopEnabled}
          onMarkerChange={handleMarker}
          audioContext={ctx}
        />
      </section>

      <section className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Trim / Fade / Normalize / Reverse
        </div>
        <TrimControls state={trimState} onChange={setTrimState} />
        <label className="inline-flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
          <input
            type="checkbox"
            checked={loopEnabled}
            onChange={(e) => setLoopEnabled(e.target.checked)}
            className="accent-yellow-300"
          />
          <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500">
            Loop between markers (audition)
          </span>
        </label>
      </section>

      <section className="space-y-2" data-testid="preview-section">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Preview (post-trim)
        </div>
        <WaveformPad
          buffer={previewBuffer}
          inMarkerFrame={0}
          outMarkerFrame={previewBuffer?.length ?? 0}
          audioContext={ctx}
        />
      </section>
    </div>
  );
}
